import { apiClient, PaginatedResponse, QueryParams } from '@/services/apiClient';
import {
  AdCampaign,
  AdCampaignDisplayNowRequest,
  AdCampaignDisplayNowResponse,
  AdCampaignPreviewRequest,
  AdCampaignStatus,
  CreateAdCampaignRequest
} from '@/types/ads';
import { AxiosRequestConfig } from 'axios';

export interface AdCampaignQueryParams extends QueryParams {
  status?: AdCampaignStatus;
  statusFilter?: 'ALL' | 'ACTIVE' | 'ARCHIVED';
  type?: 'VIDEO' | 'BANNER_OVERLAY';
  startAtFrom?: string;
  startAtTo?: string;
  channelId?: string;
  includeArchived?: boolean;
}

const withIfMatch = (version?: number): AxiosRequestConfig | undefined => {
  if (typeof version !== 'number') return undefined;
  return { headers: { 'If-Match': String(version) } };
};

export const adCampaignService = {
  async getAdCampaigns(params: AdCampaignQueryParams = {}): Promise<PaginatedResponse<AdCampaign>> {
    return apiClient.getPaginated<AdCampaign>('/ad-campaigns', params);
  },

  async getAdCampaignById(id: string): Promise<AdCampaign> {
    return apiClient.getById<AdCampaign>('/ad-campaigns', id);
  },

  async createAdCampaign(data: CreateAdCampaignRequest): Promise<AdCampaign> {
    return apiClient.create<CreateAdCampaignRequest, AdCampaign>('/ad-campaigns', data);
  },

  async updateAdCampaign(id: string, data: Partial<CreateAdCampaignRequest> & { version: number }): Promise<AdCampaign> {
    return apiClient.put<AdCampaign>(`/ad-campaigns/${id}`, data, withIfMatch(data.version));
  },

  async updateAdCampaignStatus(id: string, status: AdCampaignStatus, version: number): Promise<AdCampaign> {
    return apiClient.patch<AdCampaign>(`/ad-campaigns/${id}/status`, { status, version }, withIfMatch(version));
  },

  async previewAdCampaign(data: AdCampaignPreviewRequest): Promise<unknown> {
    return apiClient.post<unknown>('/ad-campaigns/preview', data);
  },

  async displayNow(id: string, data: AdCampaignDisplayNowRequest): Promise<AdCampaignDisplayNowResponse> {
    return apiClient.post<AdCampaignDisplayNowResponse>(`/ad-campaigns/${id}/display-now`, data);
  },

  async deleteAdCampaign(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/ad-campaigns', id);
  },
};
